// The Merlin Visibility Pane's filter bar: search across titles + message
// content, one multi-select per dimension (with facet counts under the OTHER
// filters), feedback, a date window on last activity, and the archived toggle.
// Presentational: the page owns the state and applies the transitions from
// monitorFilters.ts.

import { useEffect, useState } from 'react';

import type { MonitorFacets, StreamCatalogEntry } from '../../services/merlinMonitor';
import { MONITOR_CHANNELS, MONITOR_STATES } from '../../services/merlinMonitor';
import type { StreamKind } from '../../services/merlinMonitor';
import { MultiSelect, type MultiSelectOption } from './MultiSelect';
import { StreamBadge } from './StreamBadge';
import { countActiveFilters, type MonitorFilterState } from './monitorFilters';
import styles from './MonitorFilterBar.module.css';

export interface MonitorFilterBarProps {
  state: MonitorFilterState;
  facets: MonitorFacets | null;
  streams: StreamCatalogEntry[];
  onChange: (next: MonitorFilterState) => void;
  onReset: () => void;
}

const FEEDBACK_OPTIONS: { value: MonitorFilterState['feedback']; label: string }[] = [
  { value: '', label: 'Any feedback' },
  { value: 'any', label: 'Has a reaction' },
  { value: 'up', label: 'Has 👍' },
  { value: 'down', label: 'Has 👎' },
  { value: 'none', label: 'No reaction' },
];

function counts(list: { value: string; count: number }[] | undefined): Map<string, number> {
  return new Map((list ?? []).map((f) => [f.value, f.count]));
}

export function MonitorFilterBar({ state, facets, streams, onChange, onReset }: MonitorFilterBarProps) {
  // Debounce the search box: the URL + queries follow ~300ms behind typing.
  const [draftQ, setDraftQ] = useState(state.q);
  useEffect(() => setDraftQ(state.q), [state.q]);
  useEffect(() => {
    if (draftQ === state.q) return;
    const t = setTimeout(() => onChange({ ...state, q: draftQ, page: 0 }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire on draft change only
  }, [draftQ]);

  const set = <K extends keyof MonitorFilterState>(key: K, value: MonitorFilterState[K]) =>
    onChange({ ...state, [key]: value, page: 0 });

  const toggleIn = <K extends 'streams' | 'profileIds' | 'states' | 'models' | 'ownerIds' | 'channels'>(
    key: K,
    value: MonitorFilterState[K][number],
  ) => {
    const list = state[key] as string[];
    const next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
    set(key, next as MonitorFilterState[K]);
  };

  const streamCounts = counts(facets?.streams);
  const streamOptions: MultiSelectOption[] = streams
    .filter((s) => s.kind !== 'unknown' || streamCounts.has('unknown'))
    .map((s) => ({ value: s.kind, label: s.label, count: streamCounts.get(s.kind) ?? 0 }));

  const profileOptions: MultiSelectOption[] = (facets?.profiles ?? []).map((f) => ({
    value: f.value,
    label: f.label,
    count: f.count,
    hint: f.value || undefined,
  }));

  const stateCounts = counts(facets?.states);
  const stateOptions: MultiSelectOption[] = MONITOR_STATES.map((s) => ({
    value: s,
    label: s.replace('_', ' '),
    count: stateCounts.get(s) ?? 0,
  }));

  const modelOptions: MultiSelectOption[] = (facets?.models ?? []).map((f) => ({
    value: f.value,
    label: f.label,
    count: f.count,
  }));

  const ownerOptions: MultiSelectOption[] = (facets?.owners ?? []).map((o) => ({
    value: o.user_id,
    label: o.name || o.email || o.user_id,
    hint: o.name && o.email ? o.email : undefined,
    count: o.count,
  }));

  const channelCounts = counts(facets?.channels);
  const channelOptions: MultiSelectOption[] = MONITOR_CHANNELS.map((c) => ({
    value: c,
    label: c.toUpperCase(),
    count: channelCounts.get(c) ?? 0,
  }));

  const active = countActiveFilters(state);

  return (
    <div className={styles.bar} data-testid="monitor-filter-bar">
      <input
        type="search"
        className={styles.search}
        placeholder="Search titles and every message…"
        value={draftQ}
        onChange={(e) => setDraftQ(e.target.value)}
        aria-label="Search conversations"
      />
      <MultiSelect
        label="Stream"
        testId="filter-stream"
        options={streamOptions}
        selected={state.streams}
        onToggle={(v) => toggleIn('streams', v as StreamKind)}
        onClear={() => set('streams', [])}
        searchThreshold={99}
        renderOption={(opt) => <StreamBadge stream={opt.value as StreamKind} label={opt.label} />}
      />
      <MultiSelect
        label="Agent"
        testId="filter-profile"
        options={profileOptions}
        selected={state.profileIds}
        onToggle={(v) => toggleIn('profileIds', v)}
        onClear={() => set('profileIds', [])}
      />
      <MultiSelect
        label="State"
        testId="filter-state"
        options={stateOptions}
        selected={state.states}
        onToggle={(v) => toggleIn('states', v as MonitorFilterState['states'][number])}
        onClear={() => set('states', [])}
        searchThreshold={99}
      />
      <MultiSelect
        label="Model"
        testId="filter-model"
        options={modelOptions}
        selected={state.models}
        onToggle={(v) => toggleIn('models', v)}
        onClear={() => set('models', [])}
      />
      <MultiSelect
        label="Started by"
        testId="filter-owner"
        options={ownerOptions}
        selected={state.ownerIds}
        onToggle={(v) => toggleIn('ownerIds', v)}
        onClear={() => set('ownerIds', [])}
      />
      <MultiSelect
        label="Channel"
        testId="filter-channel"
        options={channelOptions}
        selected={state.channels}
        onToggle={(v) => toggleIn('channels', v as MonitorFilterState['channels'][number])}
        onClear={() => set('channels', [])}
        searchThreshold={99}
      />
      <select
        className={styles.select}
        value={state.feedback}
        onChange={(e) => set('feedback', e.target.value as MonitorFilterState['feedback'])}
        aria-label="Feedback"
        data-testid="filter-feedback"
      >
        {FEEDBACK_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <label className={styles.dateLabel}>
        <span>From</span>
        <input
          type="date"
          className={styles.date}
          value={state.since}
          max={state.until || undefined}
          onChange={(e) => set('since', e.target.value)}
          aria-label="Active since"
        />
      </label>
      <label className={styles.dateLabel}>
        <span>To</span>
        <input
          type="date"
          className={styles.date}
          value={state.until}
          min={state.since || undefined}
          onChange={(e) => set('until', e.target.value)}
          aria-label="Active until"
        />
      </label>
      <label className={styles.check}>
        <input
          type="checkbox"
          checked={state.includeArchived}
          onChange={(e) => set('includeArchived', e.target.checked)}
        />
        Archived
      </label>
      {active > 0 && (
        <button type="button" className={styles.reset} onClick={onReset} data-testid="filter-reset">
          Clear {active} {active === 1 ? 'filter' : 'filters'}
        </button>
      )}
    </div>
  );
}
